import {
  Div,
  Dropdown,
  HeaderIconWrapper,
  Icon as IconComponent,
  Overlay,
  Separator,
  TextButton,
  ThreeDots,
  overlayAnimationVariant,
} from '@vgrubs/components';
import {
  Fragment,
  ReactNode,
  SVGProps,
  useEffect,
  useId,
  useState,
} from 'react';

export const AdminOptionsDropdown = ({
  actions = [],
  width = '220px',
  trigger,
}: {
  actions: {
    label: string;
    onClick: () => void;
    Icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
    disabled?: boolean;
    show?: boolean;
    color?: string;
  }[];
  width?: string;
  /** What sits in the round button; the three dots when left out. */
  trigger?: ReactNode;
}) => {
  const [dropdown, setDropdown] = useState(false);
  const menuId = useId();

  const handleOnClick = (onClick: () => void) => {
    setDropdown(false);
    onClick();
  };

  // a11y: ESC closes the menu (standard WAI-ARIA menu behavior).
  // Open/close still drives focus via the trigger button itself —
  // when the user clicks/activates the trigger, focus stays there;
  // they then tab into the menu items (each is a real <button>).
  useEffect(() => {
    if (!dropdown) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setDropdown(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dropdown]);

  return (
    <>
      <Dropdown
        padding="16px 20px"
        isOpen={dropdown}
        toLeft
        triggerComponent={
          <HeaderIconWrapper
            $opened={dropdown}
            // a11y: HeaderIconWrapper itself now defaults to
            // role="button" + tabIndex=0 + Enter/Space handler. We only
            // add the dropdown-specific menu semantics here.
            aria-haspopup="menu"
            aria-expanded={dropdown}
            aria-controls={menuId}
            aria-label="More actions"
            onClick={() => setDropdown((prev) => !prev)}
          >
            {trigger ?? <ThreeDots />}
          </HeaderIconWrapper>
        }
      >
        <Div
          id={menuId}
          role="menu"
          $width="220px"
          $flex
          $column
          $alignStart
          $gap="8px"
          $gapSM="8px"
        >
          {actions.map(
            // eslint-disable-next-line array-callback-return
            ({ onClick, disabled, label, show = true, Icon, color }, i) => {
              if (show)
                return (
                  <Fragment key={i}>
                    <TextButton
                      role="menuitem"
                      onClick={() => handleOnClick(onClick)}
                      disabled={disabled}
                      color={color}
                    >
                      {Icon && (
                        <Div $mr="16">
                          <IconComponent $size={20}>
                            <Icon />
                          </IconComponent>
                        </Div>
                      )}
                      {label}
                    </TextButton>
                    {i + 1 < actions.length && <Separator />}
                  </Fragment>
                );
            }
          )}
        </Div>
      </Dropdown>
      {dropdown && (
        <Overlay
          key="modalOverlay"
          aria-hidden="true"
          variants={overlayAnimationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => setDropdown(false)}
        />
      )}
    </>
  );
};
